
import React, { useEffect, useState, useRef } from 'react';
import { X, Zap, Heart, Type, MessageSquare, Link as LinkIcon, Palette, ExternalLink, Move, Maximize, RotateCcw, Sparkles, Ban, Paintbrush, Layout, Loader2, Pipette, AlertCircle } from 'lucide-react';
import { ArtItem, Language } from '../types';
import { MASTER_FALLBACK_IMAGE } from '../constants';

const presetColors = ['#ffffff', '#000000', '#ff9eb5', '#ff4d4d', '#4dff4d', '#4d4dff', '#ffff4d', '#ff4dff', '#4dffff'];

interface ImageModalProps {
  item: ArtItem;
  onClose: () => void;
  onAddToCart: (item: ArtItem, signature?: string, discord?: string, steamUrl?: string, font?: string, dafontLink?: string, sigPosX?: number, sigPosY?: number, sigScale?: number, sigColor?: string) => void;
  language: Language;
}

const ImageModal: React.FC<ImageModalProps> = ({ item, onClose, onAddToCart, language }) => {
  const [hasError, setHasError] = useState(false);
  const [mediaReady, setMediaReady] = useState(false);
  
  const [customName, setCustomName] = useState('');
  const [discord, setDiscord] = useState('');
  const [steamUrl, setSteamUrl] = useState('');
  const [dafontLink, setDafontLink] = useState('');
  
  const [posX, setPosX] = useState(0); 
  const [posY, setPosY] = useState(60); 
  const [scale, setScale] = useState(1.0);
  const [sigColor, setSigColor] = useState('#ffffff');
  
  const colorInputRef = useRef<HTMLInputElement>(null);

  const t = {
    pt: { 
      live: 'Obra Viva', static: 'Estático', buy: 'Adquirir Arte', personalize: 'Assinatura Personalizada',
      dafontLabel: 'Link da Fonte (Dafont)', namePlaceholder: 'Seu nome aqui...', dafontPlaceholder: 'https://www.dafont.com/pt/font.php?...',
      discord: 'Discord (para entrega)', discordPlaceholder: 'Ex: usuario#0000', steam: 'Link do Perfil Steam', steamPlaceholder: 'https://steamcommunity.com/id/...',
      adjustments: 'Ajuste do Selo', posX: 'Posição X', posY: 'Posição Y', scale: 'Tamanho', color: 'Cor da Assinatura',
      customColor: 'Cor Personalizada', reset: 'Resetar', nfs: 'Obra Indisponível para Venda',
      suggestedBg: 'Fundo Sugerido (Steam)', viewOnMarket: 'Ver no Mercado', error: 'Erro ao Carregar Mídia'
    },
    en: { 
      live: 'Live Artwork', static: 'Static Art', buy: 'Aquire Artwork', personalize: 'Custom Signature',
      dafontLabel: 'Font Link (Dafont)', namePlaceholder: 'Your name here...', dafontPlaceholder: 'https://www.dafont.com/font.php?...',
      discord: 'Discord (for delivery)', discordPlaceholder: 'Ex: user#0000', steam: 'Steam Profile Link', steamPlaceholder: 'https://steamcommunity.com/id/...',
      adjustments: 'Seal Adjustments', posX: 'X Position', posY: 'Y Position', scale: 'Scale', color: 'Signature Color',
      customColor: 'Custom Color', reset: 'Reset', nfs: 'Artwork Not for Sale',
      suggestedBg: 'Suggested Background (Steam)', viewOnMarket: 'View on Market', error: 'Media Loading Error'
    }
  }[language];

  useEffect(() => {
    setHasError(false);
    setMediaReady(false);
  }, [item.id, item.videoUrl, item.imageUrl]);

  const handleReset = () => {
    setPosX(0);
    setPosY(60);
    setScale(1.0);
    setSigColor('#ffffff');
  };

  const formatPrice = (price: number) => {
    if (item.notForSale) return t.nfs;
    if (language === 'en') return `$ ${price.toFixed(2)}`;
    return `R$ ${(price * 5).toFixed(2)}`;
  };

  const videoSource = item.videoUrl || '';
  const isVideo = videoSource.toLowerCase().endsWith('.mp4') || 
                  videoSource.includes('giphy.com') || 
                  videoSource.startsWith('blob:');

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-0 md:p-8 animate-in fade-in duration-300">
      <div className="absolute inset-0 bg-black/98" onClick={onClose} />
      
      <div 
        className="relative w-full max-w-7xl h-full max-h-screen md:max-h-[95vh] bg-black overflow-hidden shadow-2xl flex flex-col md:flex-row border border-white/5 select-none"
        onContextMenu={(e) => e.preventDefault()}
      >
        <button onClick={onClose} className="absolute top-4 right-4 md:top-6 md:right-6 z-[160] p-4 bg-zinc-900/80 backdrop-blur-md hover:bg-[#ff9eb5] hover:text-white text-zinc-500 transition-all border border-white/10 rounded-full">
          <X className="w-6 h-6" />
        </button>

        <div className="flex-[1.5] bg-[#020202] relative flex items-center justify-center min-h-[40vh] md:min-h-0 overflow-hidden p-4 md:p-10">
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-0 bg-black">
             {!hasError && !mediaReady && <Loader2 className="w-8 h-8 text-[#ff9eb5] animate-spin opacity-20" />}
          </div>

          {hasError ? (
            <div className="flex flex-col items-center justify-center gap-6 text-zinc-800 opacity-40 z-10 text-center px-4">
              <AlertCircle className="w-20 h-20" />
              <p className="text-xs font-black uppercase tracking-[0.4em]">{t.error}</p>
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center relative z-10">
              <div className="relative w-fit h-fit max-w-full max-h-full shadow-2xl border border-white/5 flex items-center justify-center bg-black overflow-hidden">
                <div 
                  className="absolute inset-0 z-20 bg-transparent cursor-default" 
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                />

                {isVideo ? (
                  <video 
                    key={`modal-vid-${item.id}-${videoSource}`}
                    src={videoSource} 
                    autoPlay loop muted playsInline 
                    disablePictureInPicture
                    controlsList="nodownload"
                    preload="auto"
                    poster={item.imageUrl}
                    onCanPlay={() => setMediaReady(true)}
                    onError={() => setHasError(true)}
                    className="max-w-full max-h-[75vh] md:max-h-[85vh] w-auto h-auto object-contain block brightness-[1.1] pointer-events-none" 
                  />
                ) : (
                  <img 
                    key={`modal-img-${item.id}-${item.imageUrl}`}
                    src={item.imageUrl || MASTER_FALLBACK_IMAGE} 
                    alt={item.title[language]} 
                    decoding="async"
                    onLoad={() => setMediaReady(true)}
                    onError={() => setHasError(true)}
                    className="max-w-full max-h-[75vh] md:max-h-[85vh] w-auto h-auto object-contain block brightness-[1.1] pointer-events-none"
                    draggable="false"
                  />
                )}
                
                {customName.trim() !== '' && (
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden z-30">
                     <div className="relative w-full h-full">
                        <span 
                          className="artistic-font opacity-95 block text-center select-none whitespace-nowrap leading-none absolute transition-all duration-100"
                          style={{
                            fontSize: 'clamp(1rem, 10cqw, 8rem)', 
                            left: `${50 + (posX / 2)}%`,
                            top: `${50 + (posY / 2)}%`,
                            transform: `translate(-50%, -50%) scale(${scale})`,
                            color: sigColor,
                            textShadow: `0 0 20px ${sigColor}88`,
                            zIndex: 50,
                            willChange: 'transform, left, top'
                          }}
                        >
                          {customName}
                        </span>
                     </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 w-full md:w-[480px] p-6 md:p-12 bg-[#050607] flex flex-col border-l border-white/5 overflow-y-auto custom-scrollbar shadow-2xl z-30">
          <div className="mb-8">
            <div className="flex gap-2 mb-4">
              <span className={`inline-flex items-center gap-2 px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em] shadow-lg ${item.isAnimated ? 'bg-[#ff9eb5] text-white' : 'bg-zinc-800 text-zinc-500'}`}>
                {item.isAnimated ? t.live : t.static}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4 uppercase tracking-tighter oil-text leading-tight">{item.title[language]}</h2>
            <div className="flex flex-wrap items-baseline gap-4">
              <p className={`${item.notForSale ? 'text-zinc-700 italic text-sm' : 'text-[#ff9eb5] text-3xl'} font-mono font-black tracking-tighter opacity-80 whitespace-nowrap`}>
                {formatPrice(item.price)}
              </p>
            </div>
          </div>

          <div className="flex-1 space-y-6">
            {!item.notForSale ? (
              <div className="space-y-6">
                {item.steamBackgroundUrl && (
                  <div className="bg-zinc-900/50 border border-white/5 p-5 space-y-3">
                    <label className="text-[9px] font-black text-zinc-500 uppercase tracking-[0.3em] flex items-center gap-2">
                      <Layout className="w-3.5 h-3.5 text-[#ff9eb5]" /> {t.suggestedBg}
                    </label>
                    <a href={item.steamBackgroundUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-black border border-zinc-800 p-4 group/bg-link hover:border-[#ff9eb5] transition-all">
                      <span className="text-[10px] font-mono text-zinc-400 truncate max-w-[200px]">{item.steamBackgroundUrl}</span>
                      <span className="text-[#ff9eb5] text-[9px] font-black uppercase tracking-widest flex items-center gap-2 group-hover/bg-link:text-white">
                        {t.viewOnMarket} <ExternalLink className="w-3 h-3" />
                      </span>
                    </a>
                  </div>
                )}

                <div className="space-y-3">
                  <label className="text-[9px] font-black text-[#ff9eb5] uppercase tracking-[0.3em] flex items-center gap-2">
                    <Type className="w-3.5 h-3.5" /> {t.personalize}
                  </label>
                  <input type="text" maxLength={15} placeholder={t.namePlaceholder} value={customName} onChange={(e) => setCustomName(e.target.value)} className="w-full bg-black border border-zinc-800 p-4 text-white placeholder-zinc-800 outline-none focus:border-[#ff9eb5] transition-all text-sm font-black artistic-font" />
                </div>

                <div className="space-y-2">
                  <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                    <LinkIcon className="w-3.5 h-3.5 text-[#ff9eb5]" /> {t.dafontLabel}
                  </label>
                  <input type="text" placeholder={t.dafontPlaceholder} value={dafontLink} onChange={(e) => setDafontLink(e.target.value)} className="w-full bg-black border border-zinc-800 p-4 text-white placeholder-zinc-800 outline-none focus:border-[#ff9eb5] transition-all text-sm font-mono" />
                </div>

                <div className="space-y-2">
                  <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                    <MessageSquare className="w-3.5 h-3.5 text-[#ff9eb5]" /> {t.discord}
                  </label>
                  <input type="text" placeholder={t.discordPlaceholder} value={discord} onChange={(e) => setDiscord(e.target.value)} className="w-full bg-black border border-zinc-800 p-4 text-white placeholder-zinc-800 outline-none focus:border-[#ff9eb5] transition-all text-sm" />
                </div>

                {customName.trim() !== '' && (
                  <div className="bg-zinc-900 p-6 border border-white/10 space-y-6 animate-in slide-in-from-top-4 duration-300">
                    <div className="flex justify-between items-center">
                      <span className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">{t.adjustments}</span>
                      <button onClick={handleReset} className="text-[#ff9eb5] hover:text-white transition-colors flex items-center gap-2 text-[8px] font-black uppercase"><RotateCcw className="w-3 h-3" /> {t.reset}</button>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <span className="text-[8px] font-black text-zinc-600 uppercase flex items-center gap-2"><Paintbrush className="w-3 h-3" /> {t.color}</span>
                        <div className="flex flex-wrap gap-2 items-center">
                          {presetColors.map(color => (
                            <button key={color} onClick={() => setSigColor(color)} className={`w-6 h-6 border transition-all ${sigColor === color ? 'border-white scale-110 shadow-[0_0_8px_rgba(255,255,255,0.3)]' : 'border-transparent'}`} style={{ backgroundColor: color }} />
                          ))}
                          <div className="relative">
                            <input ref={colorInputRef} type="color" className="absolute inset-0 w-0 h-0 opacity-0 pointer-events-none" value={sigColor} onChange={(e) => setSigColor(e.target.value)} />
                            <button onClick={() => colorInputRef.current?.click()} title={t.customColor} className={`w-6 h-6 border flex items-center justify-center transition-all ${!presetColors.includes(sigColor) ? 'border-white scale-110 shadow-[0_0_8px_rgba(255,255,255,0.3)]' : 'border-white/20'}`} style={{ backgroundColor: !presetColors.includes(sigColor) ? sigColor : 'transparent', color: !presetColors.includes(sigColor) ? 'transparent' : '#ffffff' }}>
                              <Pipette className={`w-3 h-3 ${!presetColors.includes(sigColor) ? 'opacity-0' : 'opacity-100'}`} />
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <div className="flex justify-between text-[8px] font-black text-zinc-600 uppercase"><span>{t.posX}</span><span>{posX}</span></div>
                        <input type="range" min="-100" max="100" value={posX} onChange={(e) => setPosX(parseInt(e.target.value))} className="w-full accent-[#ff9eb5]" />
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-[8px] font-black text-zinc-600 uppercase"><span>{t.posY}</span><span>{posY}</span></div>
                        <input type="range" min="-100" max="100" value={posY} onChange={(e) => setPosY(parseInt(e.target.value))} className="w-full accent-[#ff9eb5]" />
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-[8px] font-black text-zinc-600 uppercase"><span>{t.scale}</span><span>{(scale * 100).toFixed(0)}%</span></div>
                        <input type="range" min="0.5" max="2.5" step="0.1" value={scale} onChange={(e) => setScale(parseFloat(e.target.value))} className="w-full accent-[#ff9eb5]" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-10 bg-black/40 border border-white/5 opacity-40">
                <Ban className="w-16 h-16 mb-6 text-zinc-700" />
                <p className="font-black uppercase tracking-[0.3em] text-[10px] text-zinc-500">{t.nfs}</p>
              </div>
            )}
          </div>

          <div className="space-y-4 mt-8">
            {!item.notForSale && (
              <button onClick={() => { onAddToCart(item, customName, discord, steamUrl, 'Custom', dafontLink, posX, posY, scale, sigColor); onClose(); }} className="w-full bg-white hover:bg-[#ff9eb5] text-black hover:text-white font-black py-6 flex items-center justify-center gap-4 transition-all shadow-xl active:scale-95 border-b-4 border-[#ff9eb5] uppercase tracking-widest text-xs">
                <Heart className="w-6 h-6 fill-current" /> {t.buy}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageModal;
